import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CmsNavigationComponent, GlobalMessageService, GlobalMessageType, RoutingService, User } from '@spartacus/core';
import { CmsComponentData } from '@spartacus/storefront';
import { Observable, Subscription } from 'rxjs';
import { AtgFormLinksService } from './atg-form-links.service';
import { KnBrNavigationComponent } from 'src/customer-libs/kn-br-navigation/components/kn-br-navigation/kn-br-navigation.component';
import { KnBrNavigationService } from 'src/services/kn-br-navigation.service';
import { KnBrSidebarService } from 'src/services/kn-br-sidebar.service';
import { KnBrCustomerContextService } from 'src/store/kn-br-customer-context/kn-br-customer-context.service';

const EFORM_URL_PATTERN = 'eFormRender.html';
const ORDER_HISTORY_URL_SUFFIX = 'customer-360/orders';

@Component({
  selector: 'atg-navigation',
  templateUrl: './atg-navigation.component.html',
  styleUrls: ['./atg-navigation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AtgNavigationComponent extends KnBrNavigationComponent implements OnInit, OnDestroy {

  user$: Observable<User> = this.atgFormLinksService.getUser();

  private hasCustomerContext = false;
  private customerContextSub?: Subscription;

  constructor(
    protected override componentData: CmsComponentData<CmsNavigationComponent>,
    protected override service: KnBrNavigationService,
    private atgFormLinksService: AtgFormLinksService,
    sidebarService: KnBrSidebarService,
    routingService: RoutingService,
    private router: Router,
    private knBrCustomerContextService: KnBrCustomerContextService,
    private globalMessageService: GlobalMessageService
  ) {
    super(componentData, service, sidebarService, routingService);
  }

  ngOnInit(): void {
    this.customerContextSub = this.knBrCustomerContextService.getActiveCustomerNumber().subscribe((customerNumber) => {
      this.hasCustomerContext = !!customerNumber;
    });
  }

  ngOnDestroy(): void {
    this.customerContextSub?.unsubscribe();
  }

  isEFormUrl(url: string): boolean {
    return !!url?.includes(EFORM_URL_PATTERN);
  }

  resolveUrl(url: string, user: User): string | null {
    if (this.isOrderHistoryLink(url) && !this.hasCustomerContext) {
      return null;
    }
    if (!url || !this.isEFormUrl(url)) return url;
    return url
      .replace(/\{0\}/g, encodeURIComponent(user?.uid ?? ''))
      .replace(/\{1\}/g, encodeURIComponent(user?.firstName ?? ''))
      .replace(/\{2\}/g, encodeURIComponent(user?.lastName ?? ''));
  }

  private isOrderHistoryLink(url: string): boolean {
    return !!url && (url === ORDER_HISTORY_URL_SUFFIX || url.endsWith(`/${ORDER_HISTORY_URL_SUFFIX}`));
  }

  shouldShowMenu(node: any, user: User): boolean {
    return true;
  }

  onMenuClick(event: Event, menu: any, index: number): void {
    if (this.isOrderHistoryLink(menu?.url) && !this.hasCustomerContext) {
      event.preventDefault();
      event.stopPropagation();
      this.redirectToSwitchAccountWithContextRequiredMessage();
      return;
    }

    if (!this.hasSubChildren(menu)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.toggleParentMenu(menu, index);
  }

  onSubmenuClick(event: Event, submenu: any): void {
    if (this.isOrderHistoryLink(submenu?.url) && !this.hasCustomerContext) {
      event.preventDefault();
      event.stopPropagation();
      this.redirectToSwitchAccountWithContextRequiredMessage();
    }
  }

  private redirectToSwitchAccountWithContextRequiredMessage(): void {
    this.knBrCustomerContextService.markContextRequiredRedirect();
    this.router.navigate(['/switch-account']);
    this.globalMessageService.add({ key: 'customer.context.required' }, GlobalMessageType.MSG_TYPE_ERROR);
  }

  hasSubChildren(menu: any): boolean {
    return Array.isArray(menu?.children?.[0]?.children) && menu.children[0].children.length > 0;
  }

  getMenuUrl(menu: any, user: User): string | null {
    if (this.hasSubChildren(menu)) {
      return null;
    }

    return this.resolveUrl(menu?.url, user);
  }

  private toggleParentMenu(menu: any, index: number): void {
    menu.active = !menu.active;
  }

  private toBoolean(value: unknown): boolean {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'number') {
      return value === 1;
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'true' || normalized === 'y' || normalized === 'yes' || normalized === '1';
    }
    return false;
  }
}
